import React, { forwardRef, useCallback } from 'react'

import Link from 'next/link'
import {
  HiMiniChevronDown,
  HiMiniPlus,
  HiOutlinePencilSquare,
} from 'react-icons/hi2'
import { LuCheckCircle, LuFolderOpen, LuUpload } from 'react-icons/lu'
import { MdOutlineAutoAwesome } from 'react-icons/md'
import { twMerge } from 'tailwind-merge'

import Button, { type ButtonProps } from '@/components/common/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/common/DropdownMenu'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

interface EditButtonProps extends React.ComponentPropsWithoutRef<'button'> {}

export const EditButton = forwardRef<HTMLButtonElement, EditButtonProps>(
  ({ onClick, children, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        variant="outline-compact"
        type="button"
        onClick={onClick}
        {...props}
      >
        {children || (
          <>
            <HiOutlinePencilSquare size={16} />
            <span>Edit</span>
          </>
        )}
      </Button>
    )
  },
)

EditButton.displayName = 'LibraryV2.Ui.Button.EditButton'

export const ConfirmSuggestionButton: React.FC<
  ButtonProps & { count: number }
> = ({
  onClick,
  children,
  count,
  type = 'button',
  variant = 'positive-compact',
  ...props
}) => {
  return (
    <Button type={type} variant={variant} onClick={onClick} {...props}>
      <LuCheckCircle size={16} />
      <span>Confirm</span>
    </Button>
  )
}

export const BasicBlueButton: React.FC<
  React.ComponentPropsWithoutRef<'button'>
> = ({ onClick, children, className, ...props }) => {
  return (
    <button
      onClick={onClick}
      className={twMerge(
        'flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:opacity-70',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
}

export const ScanFileButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'children'> & { label?: string }
>(
  (
    { className, variant = 'positive-compact', label = 'Scan File', ...props },
    ref,
  ) => {
    return (
      <Button
        ref={ref}
        variant={variant}
        className={twMerge('flex items-center active:scale-100', className)}
        {...props}
      >
        <MdOutlineAutoAwesome size={16} />
        <span className="text-sm font-medium">{label}</span>
      </Button>
    )
  },
)

ScanFileButton.displayName = 'LibraryV2.Ui.Button.ScanFileButton'

export const NewPersonButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'children'> & { label?: string }
>(
  (
    { className, variant = 'primary-compact', label = 'New Person', ...props },
    ref,
  ) => {
    return (
      <Button
        ref={ref}
        variant={variant}
        className={twMerge(className)}
        {...props}
      >
        <HiMiniPlus size={16} className="flex-shrink-0" />
        <span>{label}</span>
      </Button>
    )
  },
)

NewPersonButton.displayName = 'LibraryV2.Ui.Button.NewPersonButton'

export const NewCompanyButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'children'> & { label?: string }
>(
  (
    { className, variant = 'primary-compact', label = 'New Company', ...props },
    ref,
  ) => {
    return (
      <Button
        ref={ref}
        variant={variant}
        className={twMerge(className)}
        {...props}
      >
        <HiMiniPlus size={16} className="flex-shrink-0" />
        <span>{label}</span>
      </Button>
    )
  },
)

NewCompanyButton.displayName = 'LibraryV2.Ui.Button.NewCompanyButton'

interface NewEntityDropdownButtonProps extends Omit<
  ButtonProps,
  'children' | 'onClick'
> {
  createLabel: string
  onCreate: () => void
  onImport: () => void
  importLabel?: string
  label?: string
}

export const NewEntityDropdownButton: React.FC<
  NewEntityDropdownButtonProps
> = ({
  createLabel,
  onCreate,
  onImport,
  importLabel = 'Import from Excel',
  label = 'New',
  variant = 'positive-compact',
  className,
  ...props
}) => {
  const handleCreate = useCallback(() => {
    onCreate()
  }, [onCreate])

  const handleImport = useCallback(() => {
    onImport()
  }, [onImport])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild={true}>
        <Button
          type="button"
          variant={variant}
          className={twMerge('active:scale-100', className)}
          {...props}
        >
          <HiMiniPlus size={16} className="flex-shrink-0" />
          <span>{label}</span>
          <HiMiniChevronDown size={14} className="flex-shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        <DropdownMenuItem className="gap-2" onSelect={handleCreate}>
          <HiMiniPlus size={16} className="flex-shrink-0" />
          <span>{createLabel}</span>
        </DropdownMenuItem>
        <DropdownMenuItem className="gap-2" onSelect={handleImport}>
          <LuUpload size={16} className="flex-shrink-0" />
          <span>{importLabel}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const GoToFilesButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'children'> & { label?: string }
>(
  (
    {
      className,
      variant = 'outline-compact',
      as = 'a',
      label = 'Go to Files',
      ...props
    },
    ref,
  ) => {
    const { activeOrgId } = useActiveOrgId()
    return (
      <Link href={`/orgs/${activeOrgId}/library/files`}>
        <Button
          as={as}
          ref={ref}
          variant={variant}
          className={twMerge(className)}
          {...props}
        >
          <LuFolderOpen size={16} className="flex-shrink-0" />
          <span>{label}</span>
        </Button>
      </Link>
    )
  },
)

GoToFilesButton.displayName = 'LibraryV2.Ui.Button.GoToFilesButton'

export const ImportButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'children'> & { label?: string }
>(
  (
    { className, variant = 'positive-compact', label = 'Import', ...props },
    ref,
  ) => {
    return (
      <Button
        ref={ref}
        variant={variant}
        className={twMerge(className)}
        {...props}
      >
        <LuUpload size={16} className="flex-shrink-0" />
        <span>{label}</span>
      </Button>
    )
  },
)

ImportButton.displayName = 'LibraryV2.Ui.Button.ImportButton'
